import { DownOutlined } from '@ant-design/icons';
import { useMemo, useRef } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import MultiSelectModal from '@/components/Select/MultiSelectModal';
import type { FetcherParams } from '@/components/Select/types';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useDropdownStore from '@/stores/pure/DropdownStore';
import { lowerCaseMatch } from '@/utils/util';

type Props = {
  columns: DataTableColumnT[];
  onCustomizeColumnSelect: (key: string) => void;
};

const name = 'customize-column-dropdown';

const ColumnCustomizer = (props: Props) => {
  const color = useColor();
  const ref = useRef<HTMLDivElement>(null);
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);
  const fetcher = (params?: FetcherParams) => ({
    list: props.columns
      .filter((col) => !col.canNotBeShown && !col.isDefaultColumn)
      .filter((col) => lowerCaseMatch(col.columnName, params?.searchValue))
      .map((col) => ({
        value: col.columnKey,
        searchName: col.columnName,
      })),
    cursor: null,
  });

  const values = useMemo(() => {
    return props.columns
      .filter(
        (col) => !col.canNotBeShown && !col.isDefaultColumn && col.visible,
      )
      .map((col) => col.columnKey);
  }, [props.columns]);

  return (
    <>
      <SelectedItemStyle
        a="center"
        g={6}
        onClick={() => setCurrDropdown(name)}
        ref={ref}
      >
        <Typo type="label3" color={color.gray700}>
          Edit columns
        </Typo>
        <DownOutlined style={{ fontSize: 12 }} />
      </SelectedItemStyle>
      <MultiSelectModal
        name={name}
        data={fetcher}
        mapper={(op) => op.searchName}
        values={values}
        searchOptions={{
          enable: true,
        }}
        onCheck={(_, value) => {
          props.onCustomizeColumnSelect(value as string);
        }}
        triggerEl={ref.current}
        style={{
          maxHeight: 400,
        }}
        modalOptions={{
          offset: {
            y: -6,
          },
        }}
      />
    </>
  );
};

const SelectedItemStyle = styled(Flex)`
  height: 42px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  color: ${(props) => props.theme.colors.gray700};
`;

export default ColumnCustomizer;
